import React from 'react';

import { RJSFSchema } from '@rjsf/utils';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  getPackagePersistId,
  getPackageRoomRateId,
  getRoomRateLabel,
} from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';

import LinkDealOptionsRow from './LinkDealOptionsRow';

interface Props {
  roomType: App.RoomType;
  dealOptions: Array<App.AccommodationPackage>;
  getPackageIndex: (dealOption: App.AccommodationPackage) => number;
  pendingLinks: Record<string, string>;
  expanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  expandedDealOptionIds: Array<string>;
  onDealOptionExpandedChange: (dealOptionId: string, isExpanded: boolean) => void;
  onSetRoomRateForAll: (roomRateId: string) => void;
  offer: App.Offer;
  property: App.Property;
  packageSchema: RJSFSchema;
  onPackageSaved: () => void;
  onPackageSaveFailed: (dealOptionLabel: string) => void;
  allSave: number;
}

export default function LinkDealOptionsGroup({
  roomType,
  dealOptions,
  getPackageIndex,
  pendingLinks,
  expanded,
  onExpandedChange,
  expandedDealOptionIds,
  onDealOptionExpandedChange,
  onSetRoomRateForAll,
  offer,
  property,
  packageSchema,
  onPackageSaved,
  onPackageSaveFailed,
  allSave,
}: Props) {
  const roomRates = roomType.room_rates ?? [];

  function getSelectedRoomRateId(dealOption: App.AccommodationPackage) {
    const packageId = getPackagePersistId(dealOption);
    return (packageId && pendingLinks[packageId]) || getPackageRoomRateId(dealOption) || '';
  }

  const ratePlanNames = new Set(
    dealOptions.map((dealOption) => {
      const roomRate = roomRates.find((rate) => rate.id === getSelectedRoomRateId(dealOption));
      return roomRate ? getRoomRateLabel(roomRate) : 'Not linked';
    }),
  );
  const ratePlanSummary = ratePlanNames.size === 1 ? [...ratePlanNames][0] : `Mixed · ${ratePlanNames.size} rate plans`;

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      onChange={(_event, isExpanded) => onExpandedChange(isExpanded)}
      slotProps={{ transition: { timeout: 300 } }}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', pr: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {roomType.name}
            </Typography>
            <Chip size="small" label={`${dealOptions.length} option${dealOptions.length === 1 ? '' : 's'}`} />
            {roomRates.length > 0 && <Chip size="small" color="warning" variant="outlined" label={ratePlanSummary} />}
          </Stack>
          {roomRates.length > 0 && (
            <Box onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
              <Autocomplete
                size="small"
                options={roomRates}
                getOptionLabel={(roomRate) => getRoomRateLabel(roomRate) || roomRate.id}
                value={null}
                blurOnSelect
                onChange={(_event, roomRate) => roomRate && onSetRoomRateForAll(roomRate.id)}
                sx={{ width: 240 }}
                renderInput={(params) => <TextField {...params} placeholder="Set rate plan for all" />}
              />
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {dealOptions.map((dealOption) => {
            const dealOptionId = getPackagePersistId(dealOption) ?? dealOption.name;
            return (
              <LinkDealOptionsRow
                key={dealOptionId}
                dealOption={dealOption}
                packageIndex={getPackageIndex(dealOption)}
                roomRates={roomRates}
                selectedRoomRateId={getSelectedRoomRateId(dealOption)}
                expanded={expandedDealOptionIds.includes(dealOptionId)}
                onExpandedChange={(isExpanded) => onDealOptionExpandedChange(dealOptionId, isExpanded)}
                offer={offer}
                property={property}
                packageSchema={packageSchema}
                onPackageSaved={onPackageSaved}
                onPackageSaveFailed={onPackageSaveFailed}
                allSave={allSave}
              />
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
